import { useState, useEffect } from "react";
import { UsersThree, Play, Pause, Crown, CaretLeft } from "@phosphor-icons/react";
import { PuzzleCard } from "../components/PuzzleCard";
import { useAuth } from "../hooks/useAuth";
import { useGameEngine } from "../hooks/useGameEngine";
import { StageIndicator } from "../components/StageIndicator";
import { isGuessCorrect } from "../lib/guessMatch";
import { isInIsrael } from "../lib/geo";
import { GuessInput } from "../components/GuessInput";
import { VinylRecord } from "../components/VinylRecord";
import { createRoom, joinRoom, getRoomPlayers, subscribeToRoom, updatePlayerScore, updateRoomStatus, type Room, type RoomPlayer } from "../lib/party";
import { supabase } from "../lib/supabase";
import { recordPlayedSong } from "../lib/playedSongs";
import { trackEvent } from "../lib/clarityEvents";
import { useLocale } from "../lib/i18n";

type Phase = "menu" | "lobby" | "playing" | "song-result" | "scoreboard";

const partyOptions = { revealMode: "notes" as const };
export function PartyMode({ onNavigate }: { onNavigate?: (tab: string) => void } = {}) {
  const auth = useAuth();
  const { t, locale } = useLocale();
  const engine = useGameEngine(partyOptions);
  const [phase, setPhase] = useState<Phase>("menu");
  const [currentHe, setCurrentHe] = useState<{ title_he: string | null; artist_he: string | null; title_en: string | null; artist_en: string | null } | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [players, setPlayers] = useState<RoomPlayer[]>([]);
  const [mode, setMode] = useState<"buzzer" | "turns">("buzzer");
  const [joinCode, setJoinCode] = useState("");
  const [guesses, setGuesses] = useState<string[]>([]);
  const [wrongSignal, setWrongSignal] = useState(0);
  const [songCount, setSongCount] = useState(0);
  const [sessionPlayedIds, setSessionPlayedIds] = useState<string[]>([]);
  // 2-second confirmation flash after tapping the "Copy link" button.
  const [linkCopied, setLinkCopied] = useState(false);

  // Deep-link entry point: App.tsx stashes the code in window.__partyJoinCode
  // when the user arrives via a #party/{code} hash. Consume it on mount.
  useEffect(() => {
    const code = window.__partyJoinCode;
    if (!code || !auth.user) return;
    delete window.__partyJoinCode;
    setJoinCode(code);
    (async () => {
      const result = await joinRoom(code);
      if (!result) return;
      setRoom(result.room);
      setPlayers(result.players);
      setMode(result.room.mode);
      setPhase(result.room.status === "playing" ? "playing" : "lobby");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user]);

  // Subscribe to realtime player + room status updates
  useEffect(() => {
    if (!room) return;
    const unsub = subscribeToRoom(room.id, setPlayers, (status) => {
      if (status === "playing" && phase === "lobby") {
        // Non-host player: host started the game, transition to playing
        loadNextSong();
      } else if (status === "finished") {
        setPhase("scoreboard");
      }
    });
    return unsub;
  }, [room?.id, phase]);

  if (auth.loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 rounded-full border-3 border-zinc-700 border-t-purple-500 animate-spin" />
      </div>
    );
  }

  if (!auth.user) {
    return (
      <div className="px-5 pt-8 pb-6 flex flex-col items-center gap-5">
        <div className="w-20 h-20 rounded-full glass flex items-center justify-center">
          <UsersThree size={36} className="text-zinc-500" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold text-white mb-1">{t("party.title")}</h2>
          <p className="text-sm text-zinc-400">{t("party.description")}</p>
          <p className="text-xs text-zinc-500 mt-1">{t("party.tagline")}</p>
        </div>
        <button onClick={auth.signInWithGoogle} className="btn-primary px-8 py-4 rounded-2xl font-bold">
          {t("party.signIn")}
        </button>
      </div>
    );
  }

  const handleCreate = async () => {
    const r = await createRoom(mode);
    if (!r) return;
    setRoom(r);
    const p = await getRoomPlayers(r.id);
    setPlayers(p);
    setPhase("lobby");

    // Auto-open WhatsApp with invite (deep link, not just the raw code)
    const url = `${window.location.origin}/#party/${r.code}`;
    const text = t("party.inviteText", { code: r.code, url });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const handleJoin = async () => {
    const result = await joinRoom(joinCode);
    if (!result) return;
    setRoom(result.room);
    setPlayers(result.players);
    setMode(result.room.mode);
    setPhase("lobby");
  };

  const handleShareWhatsApp = () => {
    if (!room) return;
    const url = `${window.location.origin}/#party/${room.code}`;
    const text = t("party.inviteText", { code: room.code, url });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    trackEvent("party_whatsapp_share");
  };

  /** Copy the party deep-link to the clipboard so hosts can share the room
   *  without relying on screenshot/OCR of the 5-char code. */
  const handleCopyLink = async () => {
    if (!room) return;
    const url = `${window.location.origin}/#party/${room.code}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch { /* clipboard blocked — silently ignore */ }
  };

  const handleStartGame = async () => {
    if (!room) return;
    await updateRoomStatus(room.id, "playing");
    await loadNextSong();
  };

  const loadNextSong = async (excludeIds: string[] = sessionPlayedIds) => {
    setGuesses([]);
    const includeIsraeli = await isInIsrael();
    let { data } = await supabase.rpc("get_random_song", { exclude_ids: excludeIds, include_israeli: includeIsraeli });
    if (!data?.[0]) {
      setSessionPlayedIds([]);
      ({ data } = await supabase.rpc("get_random_song", { exclude_ids: [], include_israeli: includeIsraeli }));
      if (!data?.[0]) return;
    }
    const songId = data[0].song_id;
    const newSessionIds = [...excludeIds, songId];
    setSessionPlayedIds(newSessionIds);
    setCurrentHe({ title_he: data[0].title_he ?? null, artist_he: data[0].artist_he ?? null, title_en: data[0].title_en ?? null, artist_en: data[0].artist_en ?? null });
    try {
      await engine.loadAndPlay(data[0].artist, data[0].title, { kind: "random" });
      recordPlayedSong(songId, `party:${room?.code ?? "unknown"}`, engine.track?.startSec ?? 0);
      setPhase("playing");
      trackEvent("game_start");
      setSongCount((c) => c + 1);
    } catch {
      await loadNextSong(newSessionIds); // retry with different song
    }
  };

  const handleGuess = async (artist: string, title: string) => {
    if (!engine.track || !room) return;
    const { titleMatch, artistMatch } = isGuessCorrect(engine.track.track.title, engine.track.track.artist, title, artist, currentHe?.title_he, currentHe?.artist_he);

    if (titleMatch && artistMatch) {
      setGuesses([...guesses, "correct"]);
      await updatePlayerScore(room.id, engine.score);
      const p = await getRoomPlayers(room.id);
      setPlayers(p);
      setPhase("song-result");
      await engine.reveal();
    } else {
      const newGuesses = [...guesses, "wrong"];
      setGuesses(newGuesses);
      setWrongSignal((s) => s + 1);
      const hasNext = await engine.skip();
      if (!hasNext) {
        setPhase("song-result");
        await engine.reveal();
      }
    }
  };

  const handleSkip = async () => {
    const newGuesses = [...guesses, "skip"];
    setGuesses(newGuesses);
    const hasNext = await engine.skip();
    if (!hasNext) {
      setPhase("song-result");
      await engine.reveal();
    }
  };

  const handleEndGame = async () => {
    if (!room) return;
    await updateRoomStatus(room.id, "finished");
    setPhase("scoreboard");
  };

  const isPlaying = engine.playback.status === "playing";
  const isHost = room?.host_id === auth.user?.id;

  // ── Menu ──
  if (phase === "menu") {
    const dir = locale === "he" ? "rtl" : "ltr";
    return (
      <div dir={dir} className="px-5 pt-3 pb-6 flex flex-col gap-5" style={{ color: "#fff" }}>
        <div className="flex items-center justify-between">
          <button
            onClick={() => onNavigate?.("home")}
            className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em]"
            style={{ color: "rgba(255,255,255,0.55)" }}
          >
            <CaretLeft size={12} weight="bold" />
            {t("party.title")}
          </button>
        </div>

        <div className="text-center">
          <h2 className="text-[22px] font-extrabold text-white" style={{ letterSpacing: -0.4 }}>{t("party.subtitle")}</h2>
        </div>

        {/* Game type picker — PuzzleCards */}
        <div className="w-full">
          <p className="text-[13px] font-extrabold mb-2.5" style={{ textAlign: dir === "rtl" ? "right" : "left", color: "rgba(255,255,255,0.9)" }}>{t("party.gameType")}</p>
          <div className="grid grid-cols-2 gap-2.5">
            <PuzzleCard
              badge="⚡"
              title={t("party.buzzer")}
              subtitle={t("party.buzzerDesc")}
              accent="#B658FF"
              selected={mode === "buzzer"}
              onClick={() => setMode("buzzer")}
              dir={dir}
            />
            <PuzzleCard
              badge="↻"
              title={t("party.turns")}
              subtitle={t("party.turnsDesc")}
              accent="#00E0FF"
              selected={mode === "turns"}
              onClick={() => setMode("turns")}
              dir={dir}
            />
          </div>
        </div>

        <button onClick={handleCreate} className="btn-primary w-full py-5 rounded-2xl font-bold text-base flex items-center justify-center gap-3">
          <UsersThree size={22} weight="fill" /> {t("party.createRoom")}
        </button>

        <div className="w-full flex items-center gap-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[11px] font-bold text-zinc-500 uppercase">{t("common.or")}</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <div className="w-full flex gap-2">
          <input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder={t("common.enterCode")} maxLength={4}
            className="search-dark flex-1 py-3 px-4 rounded-xl font-bold text-center text-sm tracking-[0.3em] uppercase" />
          <button onClick={handleJoin} disabled={joinCode.length < 4} className="btn-ghost px-4 rounded-xl disabled:opacity-40">
            {t("common.join")}
          </button>
        </div>
      </div>
    );
  }

  // ── Lobby ──
  if (phase === "lobby") {
    return (
      <div className="px-5 pt-5 pb-6 flex flex-col items-center gap-5">
        <div className="text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
            {t("party.modeLabel", { mode: mode === "buzzer" ? t("party.buzzer") : t("party.turns") })}
          </p>
          <h2 className="text-xl font-bold text-white mt-1">{t("party.room", { code: room?.code ?? "" })}</h2>
        </div>

        <div className="w-full glass rounded-2xl py-6 px-4 text-center">
          <p className="text-5xl font-bold text-white tracking-[0.5em] tabular-nums mb-3">{room?.code}</p>
          <button
            onClick={handleCopyLink}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-purple-300 hover:text-white bg-purple-500/10 hover:bg-purple-500/20 transition-colors mb-2"
          >
            {linkCopied ? t("party.linkCopied") : t("party.copyLink")}
          </button>
          {room && (
            <p className="text-xs text-zinc-500 break-all">{window.location.origin}/#party/{room.code}</p>
          )}
        </div>

        <button onClick={handleShareWhatsApp} className="w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white transition-colors shadow-[0_4px_20px_rgba(37,211,102,0.3)]">
          <svg width={22} height={22} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          {t("party.inviteWhatsApp")}
        </button>

        <div className="w-full glass rounded-2xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-3">
            {t("party.players", { count: players.length })}
          </p>
          {players.map((p) => (
            <div key={p.id} className="flex items-center gap-3 py-2">
              <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs font-bold text-zinc-400">
                {p.display_name[0]}
              </div>
              <span className="text-sm font-semibold text-white flex-1">{p.display_name}</span>
              {p.is_host && <span className="text-[11px] text-purple-400 font-bold uppercase">{t("party.dj")}</span>}
            </div>
          ))}
          {players.length < 2 && (
            <div className="flex items-center gap-2 mt-2">
              <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
              <p className="text-xs text-zinc-500">{t("party.waitingPlayers")}</p>
            </div>
          )}
        </div>

        {isHost && players.length >= 2 && (
          <button onClick={handleStartGame} className="btn-primary w-full py-4 rounded-2xl font-bold text-base">
            {t("party.startGame")}
          </button>
        )}

        {isHost && players.length < 2 && (
          <button onClick={handleStartGame} className="btn-ghost w-full py-3 rounded-2xl font-bold text-sm text-zinc-500">
            {t("party.startSolo")}
          </button>
        )}

        <button onClick={() => setPhase("menu")} className="text-zinc-500 text-sm">{t("common.cancel")}</button>
      </div>
    );
  }

  // ── Playing ──
  if (phase === "playing") {
    return (
      <div className="px-5 pt-4 pb-6 flex flex-col items-center">
        <div className="w-full flex justify-between items-center mb-3">
          <span className="text-[11px] font-bold text-purple-400 uppercase">{t("party.songCount", { count: songCount })}</span>
          <button onClick={handleEndGame} className="text-[11px] font-bold text-zinc-500 uppercase">{t("party.endGame")}</button>
        </div>

        <div className="w-full max-w-[200px] mb-4">
          <VinylRecord isPlaying={isPlaying} stage={engine.stage} size="sm" />
        </div>

        <div className="w-full mb-4">
          <StageIndicator currentStage={engine.stage} guesses={guesses} />
        </div>

        <div className="flex items-center gap-6 mb-4">
          <button onClick={() => isPlaying ? engine.pause() : engine.play()} disabled={!engine.ready && !isPlaying} className="btn-primary w-14 h-14 rounded-full flex items-center justify-center">
            {isPlaying ? <Pause size={24} weight="fill" /> : <Play size={24} weight="fill" />}
          </button>
          <button onClick={handleSkip} className="btn-ghost px-4 py-2.5 rounded-xl font-bold text-xs">{engine.stage >= 6 ? t("common.discover") : t("common.clue")}</button>
        </div>

        <div className="w-full"><GuessInput onGuess={handleGuess} wrongSignal={wrongSignal} /></div>

        {/* Live scoreboard */}
        <div className="w-full glass rounded-xl p-3 mt-4">
          {players.sort((a, b) => b.score - a.score).map((p, i) => (
            <div key={p.id} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2">
                {i === 0 && <Crown size={14} weight="fill" className="text-yellow-400" />}
                <span className="text-xs font-semibold text-white">{p.display_name}</span>
              </div>
              <span className="text-xs font-bold text-purple-400 tabular-nums">{p.score}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Song result ──
  if (phase === "song-result") {
    return (
      <div className="px-5 pt-5 pb-6 flex flex-col items-center gap-4">
        {engine.track && (
          <div className="text-center">
            <h3 className="text-lg font-bold text-white">
              {locale === "he" ? (currentHe?.title_he ?? engine.track.track.title) : (currentHe?.title_en ?? engine.track.track.title)}
            </h3>
            <p className="text-purple-400 font-medium text-sm">
              {locale === "he" ? (currentHe?.artist_he ?? engine.track.track.artist) : (currentHe?.artist_en ?? engine.track.track.artist)}
            </p>
          </div>
        )}

        <div className="w-full glass rounded-2xl p-4">
          {players.sort((a, b) => b.score - a.score).map((p, i) => (
            <div key={p.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <div className="flex items-center gap-2">
                {i === 0 && <Crown size={16} weight="fill" className="text-yellow-400" />}
                <span className="text-sm font-semibold text-white">{p.display_name}</span>
              </div>
              <span className="text-base font-bold text-purple-400 tabular-nums">{p.score}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-3 w-full">
          <button onClick={() => loadNextSong()} className="btn-primary flex-1 py-4 rounded-2xl font-bold">{t("common.nextSong")}</button>
          <button onClick={handleEndGame} className="btn-ghost px-5 py-4 rounded-2xl font-bold text-sm">{t("common.end")}</button>
        </div>
      </div>
    );
  }

  // ── Final scoreboard ──
  if (phase === "scoreboard") {
    const sorted = [...players].sort((a, b) => b.score - a.score);
    return (
      <div className="px-5 pt-5 pb-6 flex flex-col items-center gap-5">
        <div className="text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">{t("party.gameOver")}</p>
          <h2 className="text-xl font-bold text-white mt-1">{t("party.finalScores")}</h2>
          <p className="text-xs text-zinc-500 mt-1">{t("party.songsPlayed", { count: songCount })}</p>
        </div>

        <div className="w-full flex flex-col gap-2">
          {sorted.map((p, i) => (
            <div key={p.id} className={`glass rounded-2xl px-4 py-4 flex items-center gap-3 ${i === 0 ? "border border-yellow-500/30" : ""}`}>
              <div className="w-8 text-center">
                {i === 0 ? <Crown size={22} weight="fill" className="text-yellow-400" /> :
                 <span className="text-sm font-bold text-zinc-500">{i + 1}</span>}
              </div>
              <span className="text-base font-semibold text-white flex-1">{p.display_name}</span>
              <span className="text-lg font-bold text-purple-400 tabular-nums">{p.score}</span>
            </div>
          ))}
        </div>

        <button onClick={() => { setPhase("menu"); setRoom(null); setPlayers([]); setSongCount(0); }}
          className="btn-ghost w-full py-4 rounded-2xl font-bold">
          {t("party.newGame")}
        </button>
      </div>
    );
  }

  return null;
}
